import Store from '@store';
import { constants as config, constants } from '@config';
import { getTextMockInstance } from 'src/factories/textmock';
import { createSvgElement } from 'assets/js/helpers/getPageContent';
import { buildLinkedTextId, getLinkedTextAreaId, getLinkedTextAreaPageId, splitLinkedTextAreaLink } from 'src/helpers/text-overflow-links';
import sanitizeContent from 'src/utils/sanitize-content';
import { TextOverflowChainHandler } from './chainHandler';

class TextOverflowFactory {
  #editor;
  #canvas;
  #chunk;
  #change;
  #chainHandler;
  #store;
  #textMock;

  constructor () {
    this.#canvas = null;
    this.#editor = null;
    this.#chunk = null;
    this.#change = false;
    this.#store = Store;
    this.#chainHandler = new TextOverflowChainHandler();
    this.#textMock = getTextMockInstance();
  }

  init () {
    this.#editor = INKIVE.Tool.getInstance().editor;
    this.#canvas = this.#editor.canvas;
    this.#chunk = {};
  }

  #getTextOverflowMetadata () {
    const state = this.#store.getState();
    const chains = state.metadata?.text_overflow?.chains;
    if (!chains) return null;

    return chains ?? null;
  }

  findTextLink (elementId, pageId = INKIVE.Tool._current_design_id) {
    const { editor } = Store.getState();
    const { linkedTextElements } = editor;

    return linkedTextElements.reduce((acc, chain) => {
      if (acc) return acc;

      const index = chain.findIndex(({ id, pageId: chainPageId }) => id === elementId && pageId === chainPageId);

      if (index > -1) {
        return {
          parent: chain[index - 1],
          child: chain[index + 1]
        };
      }

      return acc;
    }, null);
  }

  findPagesInSameChain (pageId = INKIVE.Tool._current_design_id) {
    const pages = new Set();

    const chains = this.#getTextOverflowMetadata();
    if (!chains) return pages;

    for (const chain of chains) {
      const isPageInTheChain = chain.some(item => Number(item.page_id) === pageId);
      if (isPageInTheChain) {
        chain.forEach(item => {
          if (Number(item.page_id) === pageId) return;
          pages.add(Number(item.page_id));
        });
      }
    }

    return [...pages];
  }

  // based on a list of elements, It'll return a list of text elements allowed in the same chain
  filterElementsOfCurrentChain (pageId, textElements) {
    const relatedTextLinks = new Set(this.#chainHandler.getLinkedTextElementsFromPage(INKIVE.Tool._current_design_id));

    return textElements.filter(element => {
      return relatedTextLinks.has(buildLinkedTextId(pageId, element.id));
    });
  }

  addTextElementsToTextMock (currentPageId) {
    const textMock = this.#textMock.getTextMock();
    const elements = Store.getState().elements;

    // We only add Texts that are in Text Overflow chain to text mock
    const relatedTextLinks = new Set(this.#chainHandler.getLinkedTextElementsFromPage(currentPageId));

    for (const pageId in elements) {
      if (Number(pageId) === Number(currentPageId)) continue;
      const textElements = Object.keys(elements[pageId]).map(id => {
        return {
          id,
          nodeString: elements[pageId][id].nodeString
        };
      });

      for (const element of textElements) {
        if (!relatedTextLinks.has(buildLinkedTextId(pageId, element.id))) continue;

        const textLink = this.findTextLink(element.id, pageId);
        textMock.insertAdjacentHTML('beforeend', element.nodeString);

        const node = textMock.lastChild;
        node.dataset.pageId = pageId;

        if (textLink?.parent) node.dataset.parent = buildLinkedTextId(textLink.parent.pageId, textLink.parent.id);
        if (textLink?.child) node.dataset.child = buildLinkedTextId(textLink.child.pageId, textLink.child.id);
      }
    }
  }

  // The function handles rendering hidden text overflow
  // For example, assume the active page is page 1 and consider the following text link:
  // page1-text1, page1-text2, page1-text3, page2-text1, page2-text2, page2-text3

  // The text elements in page 2 are hiding from the active page:
  // page2-text1, page2-text2, page2-text3

  // Should render hidden texts when users open Text Overflow Mode or save page.
  renderHiddenTextsOverflow () {
    const headTextsInNextPage = this.#getHeadTextsInNextPage();

    if (!headTextsInNextPage?.length) return;

    headTextsInNextPage.forEach(headText => this.#renderTextLinkByHead(headText));
    this.clearChunk();
  }

  // Update content of non-active pages with newly rendered hidden text for page propagation
  getUpdatedPagesContentWithHiddenText (pagesContent, additionalTexts = []) {
    const activePageId = INKIVE.PageFactory.getActivePageId();
    const updatedPagesContent = {};

    const textMock = document.querySelector('#textmock');
    const textareas = textMock ? [...textMock.querySelectorAll('.textarea'), ...additionalTexts] : additionalTexts;
    if (!textareas.length) return updatedPagesContent;

    const virtualPages = {};
    for (const pageId in pagesContent) {
      if (!pagesContent[pageId] || pageId === activePageId) continue;
      const content = pagesContent[pageId].content;

      if (content) {
        virtualPages[pageId] = createSvgElement(pagesContent[pageId].content);
      }
    }

    for (const textarea of textareas) {
      const updatePageId = textarea.dataset.pageId;

      const virtualPage = virtualPages[updatePageId];
      if (!virtualPage) continue;

      const virtualTextarea = virtualPage.querySelector(`.main #${textarea.id}`);
      if (!virtualTextarea) continue;

      // The below code is causing serious performance issue
      // const textObject = new INKIVE.Text.Text().setDOM(textarea, true);
      // const virtualTextObject = new INKIVE.Text.Text().setDOM(virtualTextarea, true);

      const isEqual = this.#sanitizeSpace(textarea?.textContent) === this.#sanitizeSpace(virtualTextarea?.textContent) &&
        // textObject.styles?.length === virtualTextObject.styles?.length &&
        textarea.dataset.parent === virtualTextarea.dataset.parent &&
        textarea.dataset.child === virtualTextarea.dataset.child;

      if (isEqual) continue;

      const newVirtualTextarea = textarea.cloneNode(true);

      virtualTextarea.parentElement.replaceChild(newVirtualTextarea, virtualTextarea);

      updatedPagesContent[updatePageId] = this.#svgToString(virtualPage.firstChild);
    }

    return updatedPagesContent;
  }

  #sanitizeSpace (string) {
    // Replace all spaces with non-breaking spaces.
    return string.replace(/ /g, constants.text.noBreakSpace);
  }

  getCurrentChunkId (textarea) {
    const textChains = this.#chainHandler.getLinkedTextElements();
    const [pageId, id] = splitLinkedTextAreaLink(textarea.child_text || textarea.parent_text);
    const chainIndex = textChains.findIndex(chain => {
      return chain.some(link => link.id === id && link.pageId === Number(pageId));
    });

    if (chainIndex === -1) return null;

    const chunkIndex = chainIndex + 1;

    return this.#initChunk(chunkIndex, textChains[chunkIndex]);
  }

  setChunkContent (chunkId, content) {
    // Content must be array of paragraphs
    this.#chunk[chunkId] = this.#chunk[chunkId] || {};
    this.#chunk[chunkId].content = content.length ? content : [];
  }

  setChunkPartialStyles (chunkId, partialStyles) {
    // Partial Styles must be object of spread style
    this.#chunk[chunkId] = this.#chunk[chunkId] || {};
    this.#chunk[chunkId].partialStyles = partialStyles || {};
  }

  setChunkParagraphStyles (chunkId, paragraphStyles) {
    // Letter Spacing must be array
    this.#chunk[chunkId] = this.#chunk[chunkId] || {};
    this.#chunk[chunkId].paragraphStyles = paragraphStyles || {};
  }

  setChunkLetterSpacing (chunkId, letterSpacing) {
    // Letter Spacing must be array
    this.#chunk[chunkId] = this.#chunk[chunkId] || {};
    this.#chunk[chunkId].letterSpacing = letterSpacing || [];
  }

  getChunkContent (chunkId) {
    if (!chunkId) return null;
    if (!this.#chunk[chunkId]) return null;

    return this.#chunk[chunkId].content;
  }

  getChunkPartialStyles (chunkId, partialStyles) {
    if (!chunkId) return null;
    if (!this.#chunk[chunkId]) return null;

    return this.#chunk[chunkId].partialStyles;
  }

  getChunkParagraphStyles (chunkId, paragraphStyles) {
    if (!chunkId) return null;
    if (!this.#chunk[chunkId]) return null;

    return this.#chunk[chunkId].paragraphStyles;
  }

  getChunkLetterSpacing (chunkId, letterSpacing) {
    if (!chunkId) return null;
    if (!this.#chunk[chunkId]) return null;

    return this.#chunk[chunkId].letterSpacing;
  }

  getChunk (chunkId) {
    if (!chunkId) return this.#chunk;
    return this.#chunk[chunkId];
  }

  clearChunk (chunkId) {
    if (!chunkId) this.#chunk = {};
    if (!this.#chunk[chunkId]) return;

    delete this.#chunk[chunkId];
  }

  #initChunk (chunkId, textChain) {
    this.#chunk[chunkId] = this.#chunk[chunkId] || {};
    this.#chunk[chunkId].chain = textChain;
    this.#chunk[chunkId].content = this.#chunk[chunkId].content || [];
    this.#chunk[chunkId].partialStyles = this.#chunk[chunkId].partialStyles || {};
    this.#chunk[chunkId].paragraphStyles = this.#chunk[chunkId].paragraphStyles || {};
    this.#chunk[chunkId].letterSpacing = this.#chunk[chunkId].letterSpacing || [];

    return chunkId;
  }

  #getHeadTextsInNextPage () {
    const textMock = this.#textMock.getTextMock();
    if (!textMock) return [];

    const currentPageId = INKIVE.Tool._current_design_id;
    const relatedTextLinks = this.#chainHandler.getRelatedLinkedTextsFromPage(currentPageId);

    return relatedTextLinks.map(link => textMock.querySelector(`#${link.id}[data-page-id="${link.pageId}"]`)).filter(Boolean);
  }

  #renderTextLinkByHead (headText) {
    const linkedTextList = new INKIVE.Text.LinkedTextList();
    let currentTextarea = new INKIVE.Text.Textarea().setDOM(headText, false);
    let shouldAbortRender = false;

    // Add text to linkedTextList
    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      linkedTextList.addEnd(currentTextarea);
      const chunkId = linkedTextList.calculateCurrentChunk();
      shouldAbortRender = this.isHiddenContentEmpty(chunkId);

      const childPageId = getLinkedTextAreaPageId(currentTextarea.child_text);
      const childId = getLinkedTextAreaId(currentTextarea.child_text);

      const childText = childPageId === Number(INKIVE.Tool._current_design_id)
        ? document.querySelector(`g#${childId}`)
        : document.querySelector(`g#${childId}[data-page-id="${childPageId}"]`);

      if (!childText) break;
      currentTextarea = new INKIVE.Text.Textarea().setDOM(childText, false);
    }

    // Render linkedTextList
    linkedTextList.renderHiddenTexts(this.#canvas, shouldAbortRender);
  }

  isHiddenContentEmpty (chunkId) {
    if (!this.#chunk[chunkId]) return true;
    if (!this.#chunk[chunkId].content) return true;
    if (!this.#chunk[chunkId].content.length) return true;

    return false;
  }

  #svgToString (svg) {
    return sanitizeContent(INKIVE.DesignFacade.canvas.svgToString(svg));
  }

  hasChanges () {
    return this.#change;
  }

  hasLinkedNodes () {
    const state = this.#store.getState();
    const chains = state.metadata?.text_overflow?.chains;

    return Array.isArray(chains) && chains.length > 0;
  }

  setChange (boolean) {
    this.#change = boolean;
  }
}

export default TextOverflowFactory;
